import React, { forwardRef, useCallback } from 'react';
import styles from './RangeSlider.module.css';

export interface IRangeSliderProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /** Lower bound of the track (inclusive). */
  min: number;
  /** Upper bound of the track (inclusive) — also the hard cap on the emitted value. */
  max: number;
  /** Increment the thumb snaps to. */
  step: number;
  /** Current value. Clamped into [min, max] for display. */
  value: number;
  /** Fires with the new numeric value, already clamped into [min, max]. */
  onChange: (value: number) => void;
  /** Accessible name for the slider (aria-label on the range input). */
  label: string;
  /** Set on the range input, not the root, so an external readout can point at it. */
  id?: string;
  disabled?: boolean;
}

/**
 * RangeSlider — a thin, reusable wrapper over a native `<input type="range">`
 * (web-client #221 / Task 2.4 S7).
 *
 * The native range element already clamps to [min, max] and snaps to `step`, but the
 * component re-clamps the emitted value defensively so the `max` hard cap holds even when
 * a value is injected programmatically (e.g. fireEvent in tests, or a future controlled
 * push above max). The clamp at `max` is load-bearing for the sell flow: the slider's
 * `max = faceValue` is the hard cap that supersedes the old keystroke clamp (#210).
 */
const RangeSlider = forwardRef<HTMLDivElement, IRangeSliderProps>(
  ({ min, max, step, value, onChange, label, id, disabled = false, className, ...rest }, ref) => {
    const clamped = Math.min(Math.max(value, min), max);

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = Number(e.target.value);
        if (!Number.isFinite(raw)) {
          return;
        }
        onChange(Math.min(Math.max(raw, min), max));
      },
      [onChange, min, max],
    );

    // Fill percentage for the visual track gradient.
    const pct = max > min ? ((clamped - min) / (max - min)) * 100 : 0;

    return (
      <div ref={ref} className={`${styles.rangeSlider} ${className || ''}`} {...rest}>
        <input
          id={id}
          type="range"
          className={styles.input}
          min={min}
          max={max}
          step={step}
          value={clamped}
          onChange={handleChange}
          disabled={disabled}
          aria-label={label}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={clamped}
          style={{
            background: `linear-gradient(to right, var(--action-primary) 0%, var(--action-primary) ${pct}%, var(--border-divider) ${pct}%, var(--border-divider) 100%)`,
          }}
        />
      </div>
    );
  },
);

RangeSlider.displayName = 'RangeSlider';

export default RangeSlider;
